import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Text,
  Tr,
  Td,
  Flex,
  HStack,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { FiUsers } from "react-icons/fi";
import { listClientContacts } from "../../../api/clientsApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import ContactFormModal from "./ContactFormModal";
import { useTranslation } from "react-i18next";
import {
  CONTACT_STATUS_COLORS,
  CONTACT_STATUS_LABELS,
  CONTACT_STATUS_LABEL_KEYS,
  getStatusLabel
} from "../../../constants/statuses";
import { DataTable, StatusBadge, EmptyState } from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX
} from "../../../components/Portal/portalModalChrome";

const ClientContactsWizardModal = ({ isOpen, onClose, client }) => {
  const { selectedBusinessUnitId } = useBusinessUnit();
  const toast = useToast();
  const { t } = useTranslation("portal");

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  const createContactModal = useDisclosure();

  const clientId = client?.id;

  const loadContacts = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await listClientContacts(clientId, { buId: selectedBusinessUnitId || undefined });
      setContacts(Array.isArray(res) ? res : []);
    } catch (e) {
      toast({ title: t("pilotClients.contactsWizard.toasts.loadContactsFailed"), status: "error" });
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedBusinessUnitId, toast, t]);

  useEffect(() => {
    if (!isOpen || !clientId) return;
    loadContacts();
  }, [isOpen, clientId, loadContacts]);

  const handleContactCreated = () => {
    // Refresh list after creating a contact
    loadContacts();
  };

  const handleClose = () => {
    onClose?.();
  };

  const columns = [
    { key: "name", label: t("pilotClients.detail.contacts.table.name"), width: "30%" },
    { key: "status", label: t("pilotClients.detail.contacts.table.status"), width: "24%" },
    { key: "email", label: t("pilotClients.detail.contacts.table.email"), width: "28%" },
    { key: "phone", label: t("pilotClients.detail.contacts.table.phone"), width: "18%" }
  ];

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="xl">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {t("pilotClients.contactsWizard.modalTitle", {
              clientName: client?.name || ""
            })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="16px">
            <Text fontSize="13px" color="portal.muted" sx={{ marginBottom: "14px !important" }}>
              {t("pilotClients.contactsWizard.intro")}
            </Text>
            <Flex justify="space-between" align="center" mb="10px">
              <Text
                fontSize="10px"
                textTransform="uppercase"
                fontWeight="800"
                letterSpacing="0.055em"
                color="portal.muted"
                sx={{ marginBottom: "0 !important" }}
              >
                {t("pilotClients.contactsWizard.header")}
              </Text>
              <Button
                size="sm"
                variant="primary"
                onClick={createContactModal.onOpen}
                isDisabled={!clientId}
              >
                {t("pilotClients.contactsWizard.addContactButton")}
              </Button>
            </Flex>
            <Box
              bg="portal.panel"
              borderRadius="10px"
              border="1px solid"
              borderColor="portal.line"
              overflow="hidden"
            >
              <Box overflowX="auto">
                <DataTable
                  columns={columns}
                  rows={contacts}
                  loading={loading}
                  loadingRows={3}
                  emptyState={
                    <EmptyState
                      icon={<FiUsers />}
                      title={t("pilotClients.contactsWizard.table.noContacts")}
                      hint={t("pilotClients.contactsWizard.intro")}
                    />
                  }
                  renderRow={(c) => (
                    <Tr key={c.id}>
                      <Td fontWeight="700" color="portal.text">{c.name || "-"}</Td>
                      <Td>
                        {c.status ? (
                          <StatusBadge
                            colorScheme={CONTACT_STATUS_COLORS[c.status] || "gray"}
                            label={getStatusLabel(
                              t,
                              CONTACT_STATUS_LABEL_KEYS,
                              CONTACT_STATUS_LABELS,
                              c.status
                            )}
                          />
                        ) : (
                          "-"
                        )}
                      </Td>
                      <Td>{c.email || "-"}</Td>
                      <Td>{c.phone || "-"}</Td>
                    </Tr>
                  )}
                />
              </Box>
            </Box>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <HStack spacing="8px">
              <Button variant="secondary" size="sm" onClick={handleClose}>
                {t("pilotClients.contactsWizard.buttons.skip")}
              </Button>
              <Button variant="primary" size="sm" onClick={handleClose}>
                {t("pilotClients.contactsWizard.buttons.finish")}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {clientId && (
        <ContactFormModal
          isOpen={createContactModal.isOpen}
          onClose={createContactModal.onClose}
          initialData={null}
          clientId={clientId}
          onSuccess={handleContactCreated}
        />
      )}
    </>
  );
};

export default ClientContactsWizardModal;
